import React, { useState, useEffect } from 'react';
import { FeatureRequestService } from '@/services/feature-request.service';
import {
  FeatureRequest,
  FeatureRequestStatus,
  FeatureRequestPriority,
} from '@/models/feature-request.model';
import { useAuth } from '@/contexts/AuthContext';

const StatusLabels: Record<FeatureRequestStatus, string> = {
  [FeatureRequestStatus.submitted]: 'Submitted',
  [FeatureRequestStatus.underReview]: 'Under Review',
  [FeatureRequestStatus.planned]: 'Planned',
  [FeatureRequestStatus.inDevelopment]: 'In Development',
  [FeatureRequestStatus.completed]: 'Completed',
  [FeatureRequestStatus.rejected]: 'Rejected',
  [FeatureRequestStatus.duplicate]: 'Duplicate',
};

const PriorityLabels: Record<FeatureRequestPriority, string> = {
  [FeatureRequestPriority.low]: 'Low',
  [FeatureRequestPriority.medium]: 'Medium',
  [FeatureRequestPriority.high]: 'High',
  [FeatureRequestPriority.critical]: 'Critical',
};

export const FeatureRequests: React.FC = () => {
  const { profile } = useAuth();
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FeatureRequestStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<FeatureRequestPriority | 'all'>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);

  useEffect(() => {
    const unsubscribe = FeatureRequestService.subscribeToFeatureRequests((data) => {
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...requests];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(r => r.status === filterStatus);
    }

    if (filterPriority !== 'all') {
      filtered = filtered.filter(r => r.priority === filterPriority);
    }

    // Sort by votes (descending) then by created date
    filtered.sort((a, b) => {
      if (b.votes !== a.votes) return b.votes - a.votes;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    setFilteredRequests(filtered);
  }, [requests, searchQuery, filterStatus, filterPriority]);

  const handleVote = async (requestId: string) => {
    if (!profile) {
      alert('Please log in to vote');
      return;
    }
    try {
      await FeatureRequestService.voteForFeatureRequest(requestId, profile.id);
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to vote');
    }
  };

  const handleViewRequest = async (request: FeatureRequest) => {
    const fullRequest = await FeatureRequestService.getFeatureRequestById(request.id);
    if (fullRequest) {
      setSelectedRequest(fullRequest);
    }
  };

  const handleAddComment = async (requestId: string, content: string, isInternal: boolean) => {
    if (!profile) return;
    
    try {
      await FeatureRequestService.addComment(requestId, {
        authorId: profile.id,
        authorName: profile.fullName,
        authorEmail: profile.email,
        isInternal,
        content,
      });
      
      const updated = await FeatureRequestService.getFeatureRequestById(requestId);
      if (updated) setSelectedRequest(updated);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleUpdateStatus = async (requestId: string, status: FeatureRequestStatus) => {
    try {
      await FeatureRequestService.updateFeatureRequest(requestId, { status });
      const updated = await FeatureRequestService.getFeatureRequestById(requestId);
      if (updated) setSelectedRequest(updated);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  // Calculate stats
  const stats = {
    total: requests.length,
    submitted: requests.filter(r => r.status === FeatureRequestStatus.submitted).length,
    inDevelopment: requests.filter(r => r.status === FeatureRequestStatus.inDevelopment).length,
    completed: requests.filter(r => r.status === FeatureRequestStatus.completed).length,
    totalVotes: requests.reduce((sum, r) => sum + r.votes, 0),
  };

  if (loading && requests.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading feature requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Feature Requests</h1>
            <p className="mt-1 text-sm text-gray-500">Track and prioritize feature requests from users</p>
          </div>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Request
          </button>
        </div>

        {/* Stats Cards */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard label="Total Requests" value={stats.total} icon="💡" color="blue" />
            <StatCard label="Submitted" value={stats.submitted} icon="📝" color="yellow" />
            <StatCard label="In Development" value={stats.inDevelopment} icon="⚙️" color="purple" />
            <StatCard label="Completed" value={stats.completed} icon="✅" color="green" />
            <StatCard label="Total Votes" value={stats.totalVotes} icon="👍" color="indigo" />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search feature requests..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FeatureRequestStatus | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                {Object.entries(StatusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as FeatureRequestPriority | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                {Object.entries(PriorityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Feature Request List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Feature Requests ({filteredRequests.length})
            </h3>
          </div>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No feature requests found</h3>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <FeatureRequestCard
                  key={request.id}
                  request={request}
                  onView={() => handleViewRequest(request)}
                  onVote={() => handleVote(request.id)}
                  hasVoted={profile ? request.voters.includes(profile.id) : false}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feature Request Detail Modal */}
      {selectedRequest && (
        <FeatureRequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onAddComment={handleAddComment}
          onUpdateStatus={handleUpdateStatus}
          onVote={() => handleVote(selectedRequest.id)}
          hasVoted={profile ? selectedRequest.voters.includes(profile.id) : false}
        />
      )}

      {/* Create Dialog */}
      {showCreateDialog && (
        <CreateFeatureRequestDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
          }}
        />
      )}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'purple' | 'indigo';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="text-3xl opacity-75">{icon}</div>
      </div>
    </div>
  );
};

interface FeatureRequestCardProps {
  request: FeatureRequest;
  onView: () => void;
  onVote: () => void;
  hasVoted: boolean;
}

const FeatureRequestCard: React.FC<FeatureRequestCardProps> = ({
  request,
  onView,
  onVote,
  hasVoted,
}) => {
  const getPriorityColor = (priority: FeatureRequestPriority) => {
    switch (priority) {
      case FeatureRequestPriority.critical:
        return 'bg-red-100 text-red-800';
      case FeatureRequestPriority.high:
        return 'bg-orange-100 text-orange-800';
      case FeatureRequestPriority.medium:
        return 'bg-yellow-100 text-yellow-800';
      case FeatureRequestPriority.low:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: FeatureRequestStatus) => {
    switch (status) {
      case FeatureRequestStatus.completed:
        return 'bg-green-100 text-green-800';
      case FeatureRequestStatus.inDevelopment:
        return 'bg-blue-100 text-blue-800';
      case FeatureRequestStatus.planned:
        return 'bg-purple-100 text-purple-800';
      case FeatureRequestStatus.rejected:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1 cursor-pointer" onClick={onView}>
          <div className="flex items-center space-x-2">
            <h4 className="text-lg font-semibold text-gray-900">{request.title}</h4>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(request.priority)}`}>
              {PriorityLabels[request.priority]}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
              {StatusLabels[request.status]}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600 line-clamp-2">{request.description}</p>
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
            <span>Submitted by {request.submittedByName || request.submittedByEmail || 'Unknown'}</span>
            {request.assignedToName && (
              <>
                <span>•</span>
                <span>Assigned to {request.assignedToName}</span>
              </>
            )}
            {request.targetRelease && (
              <>
                <span>•</span>
                <span>Target: {request.targetRelease}</span>
              </>
            )}
            <span>•</span>
            <span>{new Date(request.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onVote();
          }}
          className={`ml-4 flex items-center space-x-1 px-3 py-2 rounded-md ${
            hasVoted
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.834a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
          <span className="font-semibold">{request.votes}</span>
        </button>
      </div>
    </div>
  );
};

interface FeatureRequestDetailModalProps {
  request: FeatureRequest;
  onClose: () => void;
  onAddComment: (requestId: string, content: string, isInternal: boolean) => void;
  onUpdateStatus: (requestId: string, status: FeatureRequestStatus) => void;
  onVote: () => void;
  hasVoted: boolean;
}

const FeatureRequestDetailModal: React.FC<FeatureRequestDetailModalProps> = ({
  request,
  onClose,
  onAddComment,
  onUpdateStatus,
  onVote,
  hasVoted,
}) => {
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  const handleSubmitComment = () => {
    if (!comment.trim()) return;
    onAddComment(request.id, comment, isInternal);
    setComment('');
    setIsInternal(false);
    setShowCommentForm(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{request.title}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <button
                  onClick={onVote}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                    hasVoted
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.834a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                  </svg>
                  <span className="font-semibold">{request.votes} votes</span>
                </button>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <p className="text-gray-700 whitespace-pre-wrap">{request.description}</p>
            </div>

            {(request.estimatedEffort || request.targetRelease) && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Implementation Details</h4>
                <div className="space-y-1 text-sm">
                  {request.estimatedEffort && (
                    <p><span className="font-medium">Estimated Effort:</span> {request.estimatedEffort}</p>
                  )}
                  {request.targetRelease && (
                    <p><span className="font-medium">Target Release:</span> {request.targetRelease}</p>
                  )}
                  {request.actualRelease && (
                    <p><span className="font-medium">Actual Release:</span> {request.actualRelease}</p>
                  )}
                </div>
              </div>
            )}

            <div className="mb-6 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments</h3>
              <div className="space-y-4">
                {request.comments.map((comment) => (
                  <div key={comment.id} className={`p-4 rounded-lg ${comment.isInternal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm">{comment.authorName}</span>
                      {comment.isInternal && (
                        <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">Internal</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{comment.content}</p>
                    <p className="text-xs text-gray-500 mt-2">{new Date(comment.createdAt).toLocaleString()}</p>
                  </div>
                ))}
              </div>

              {!showCommentForm ? (
                <button
                  onClick={() => setShowCommentForm(true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Comment
                </button>
              ) : (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">Internal note</span>
                    </label>
                    <div className="space-x-2">
                      <button
                        onClick={() => {
                          setShowCommentForm(false);
                          setComment('');
                        }}
                        className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitComment}
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={request.status}
                onChange={(e) => onUpdateStatus(request.id, e.target.value as FeatureRequestStatus)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
              >
                {Object.entries(StatusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CreateFeatureRequestDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateFeatureRequestDialog: React.FC<CreateFeatureRequestDialogProps> = ({
  onClose,
  onSuccess,
}) => {
  const { profile } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<FeatureRequestPriority>(FeatureRequestPriority.medium);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await FeatureRequestService.createFeatureRequest({
        title: title.trim(),
        description: description.trim(),
        priority,
        submittedBy: profile?.id || '',
        submittedByName: profile?.fullName,
        submittedByEmail: profile?.email,
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating feature request:', error);
      alert('Failed to create feature request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">New Feature Request</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as FeatureRequestPriority)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500"
              >
                {Object.entries(PriorityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

